import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useLocation, useSearchParams } from 'react-router';

import { AppRoutes, THROTTLE_LIMIT } from '@/constants';
import { formatDate } from '@/helpers';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';

import { CloseIcon, InfoCircleIcon, RefreshCcw01Icon } from '@/assets/icons';
import {
  Button,
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  SortDirection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import { JobStatus } from '@/features/regulatory-intake/enums';
import {
  getJobStatusIcon,
  getJobStatusLabel,
  getJobStatusVariant,
  getTriggerLabel,
} from '@/features/regulatory-intake/utils';

import { JOB_STATUS_OPTIONS } from '@/features/regulatory-intake/constants';
import { JobField } from '@/features/regulatory-intake/intake-list/enums';
import {
  useJobRetryMutation,
  useJobSearching,
} from '@/features/regulatory-intake/intake-list/hooks';
import { IJob, IJobSearchingPayload } from '@/features/regulatory-intake/intake-list/interfaces';

import { JobProgress } from './JobProgress';

export const Jobs = () => {
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [statuses, setStatuses] = useState<string[]>(
    searchParams.get('statuses')?.split(',').filter(Boolean) || [],
  );
  const [startFrom, setStartFrom] = useState<Date | undefined>(
    searchParams.get('from') ? new Date(searchParams.get('from') as string) : undefined,
  );
  const [startTo, setStartTo] = useState<Date | undefined>(
    searchParams.get('to') ? new Date(searchParams.get('to') as string) : undefined,
  );

  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || JobField.Id);
  const [isDescending, setDescending] = useState<boolean>(searchParams.get('desc') !== 'false');

  const previousFilterKeyRef = useRef<string | null>(null);
  const startFromPickerRef = useRef<any>(null);
  const startToPickerRef = useRef<any>(null);

  const isFiltered = useMemo(
    () =>
      searchQuery.length > 0 ||
      statuses.length > 0 ||
      startFrom !== undefined ||
      startTo !== undefined,
    [statuses, startFrom, startTo, searchQuery],
  );

  const onClearFilters = () => {
    navigate(
      {
        pathname: routerLocation.pathname,
        hash: routerLocation.hash,
      },
      { replace: true },
    );
    setSearchQuery('');
    setStatuses([]);
    setStartFrom(undefined);
    setStartTo(undefined);
    startFromPickerRef.current?.onClear();
    startToPickerRef.current?.onClear();
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // // Throttle filters and sorting
  const throttledStatuses = useThrottle(statuses, THROTTLE_LIMIT, true);
  const throttledStartFrom = useThrottle(startFrom, THROTTLE_LIMIT, true);
  const throttledStartTo = useThrottle(startTo, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Handle column sorting
  const onSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setDescending(true);
    } else {
      setSortBy(columnKey);
      setDescending(direction === 'desc');
    }
  };

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: IJobSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    // Divide by 2 because of how enums are compiled in TS
    const statusCount = Object.keys(JobStatus).length / 2;
    if (
      throttledStatuses &&
      throttledStatuses.length > 0 &&
      throttledStatuses.length < statusCount
    ) {
      payload.statuses = throttledStatuses.map((status) => Number(status) as JobStatus);
    }

    if (throttledStartFrom) {
      payload.startTime = throttledStartFrom.toISOString();
    }

    if (throttledStartTo) {
      const endOfDay = new Date(throttledStartTo);
      endOfDay.setHours(23, 59, 59, 999);
      payload.endTime = endOfDay.toISOString();
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledStatuses,
    throttledStartFrom,
    throttledStartTo,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isLoading, isFetching } = useJobSearching(apiPayload);
  const { mutate: jobRetry, isPending: isJobRetryPending } = useJobRetryMutation();

  const jobs = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata?.totalPages || 0, [data?.metadata?.totalPages]);
  const totalItems = useMemo(() => data?.metadata?.totalItems || 0, [data?.metadata?.totalItems]);
  const isEmpty = useMemo(() => jobs.length === 0, [jobs]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (statuses.length) params.set('statuses', statuses.join(','));
    if (startFrom) params.set('from', startFrom.toISOString());
    if (startTo) params.set('to', startTo.toISOString());
    if (sortBy !== JobField.Id) params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    navigate(
      {
        pathname: routerLocation.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash: routerLocation.hash,
      },
      { replace: true },
    );
  }, [
    currentPage,
    searchQuery,
    statuses,
    startFrom,
    startTo,
    sortBy,
    isDescending,
    routerLocation.pathname,
    routerLocation.hash,
  ]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledStatuses,
      throttledStartFrom,
      throttledStartTo,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      // First run — store baseline, don't reset
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [
    debouncedSearchQuery,
    throttledStatuses,
    throttledStartFrom,
    throttledStartTo,
    throttledSortBy,
    throttledIsDescending,
  ]);

  return (
    <div>
      <div className="flex justify-between w-full">
        <div className="flex flex-row flex-wrap justify-start items-center gap-2">
          <Search
            placeholder="Search by job ID"
            className="w-[340px] sm:w-[340px] gap-2"
            style={{ paddingRight: '16px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={JOB_STATUS_OPTIONS}
            defaultValue={statuses}
            placeholder="Job status"
            onValueChange={(values) => {
              setStatuses(values);
            }}
            maxCount={1}
            className="!w-fit !min-w-[280px]"
          />
          <DatePicker
            required
            ref={startFromPickerRef}
            align="start"
            className="gap-2"
            buttonClassName="!w-fit !min-w-[260px]"
            placeholder="Start date from"
            initialDate={startFrom}
            // "From" must be <= "To": disable every date after the current `startTo`.
            calendarDisabled={(day) => (startTo ? day > startTo : false)}
            onUpdate={({ date }) => setStartFrom(date)}
          />
          <DatePicker
            required
            ref={startToPickerRef}
            align="start"
            className="gap-2"
            buttonClassName="!w-fit !min-w-[260px]"
            placeholder="Start date to"
            initialDate={startTo}
            // "To" must be >= "From": disable every date before the current `startFrom`.
            calendarDisabled={(day) => (startFrom ? day < startFrom : false)}
            onUpdate={({ date }) => setStartTo(date)}
          />
          {isFiltered && (
            <Button
              buttonColor="primary"
              buttonStyle="textlink"
              prefixIcon={<CloseIcon className="size-5" />}
              onClick={onClearFilters}
              size="default"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult label="No intake jobs." />
        ) : (
          <>
            <div className="w-full pb-3 mb-3 overflow-auto custom-scrollbar">
              <Table className="mt-4 min-w-full !w-fit !overflow-hidden" size="sm" template="zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === JobField.Id ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(JobField.Id, direction)}
                    >
                      Job ID
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === JobField.Trigger ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(JobField.Trigger, direction)}
                    >
                      Trigger
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === JobField.StartTime ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(JobField.StartTime, direction)}
                    >
                      Start Time
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === JobField.EndTime ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(JobField.EndTime, direction)}
                    >
                      End Time
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === JobField.RunBy ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(JobField.RunBy, direction)}
                    >
                      Started by
                    </TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === JobField.Status ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(JobField.Status, direction)}
                    >
                      Job Status
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job: IJob) => (
                    <TableRow key={job.id} rowId={job.id}>
                      <TableCell className="font-semibold underline text-primary-base min-w-[126px] max-w-[9%]">
                        <Link
                          to={generatePath(AppRoutes.REGULATORY_INTAKE_DETAILS, {
                            id: job.id,
                          })}
                          className="block truncate"
                          title={job.id}
                        >
                          {job.id}
                        </Link>
                      </TableCell>
                      <TableCell className="min-w-[120px] text-neutral-9 max-w-[8%]">
                        {getTriggerLabel(job.triggerMode)}
                      </TableCell>
                      <TableCell className="min-w-[152px] w-[152px] whitespace-break-spaces text-neutral-9">
                        {job.startTime ? (
                          formatDate({
                            date: new Date(job.startTime),
                            hasTime: true,
                            hasTimezone: true,
                          })
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[152px] w-[152px] whitespace-break-spaces text-neutral-9">
                        {job.endTime ? (
                          formatDate({
                            date: new Date(job.endTime),
                            hasTime: true,
                            hasTimezone: true,
                          })
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[160px] text-neutral-9 max-w-[11%]">
                        {job.runBy || 'System'}
                      </TableCell>
                      <TableCell className="min-w-[390px] text-neutral-9 max-w-[30%]">
                        {job.totalMessages &&
                        job.status &&
                        [
                          JobStatus.Running,
                          JobStatus.Completed,
                          JobStatus.CompletedWithErrors,
                        ].includes(job.status) ? (
                          <JobProgress
                            progressCount={job.messagesInProgress || 0}
                            totalCount={job.totalMessages}
                            status={job.status || JobStatus.NotAvailable}
                          />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell
                        cellType="status"
                        className="min-w-[200px] max-w-[14%]"
                        statusVariant={getJobStatusVariant(job.status || JobStatus.NotAvailable)}
                        statusIcon={(() => {
                          const Icon = getJobStatusIcon(job.status || JobStatus.NotAvailable);
                          return <Icon className="size-3" />;
                        })()}
                      >
                        {getJobStatusLabel(job.status || JobStatus.NotAvailable)}
                      </TableCell>
                      <TableCell
                        align="right"
                        cellType="custom"
                        className="!p-0 min-w-[92px] relative max-w-[6%]"
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            className="w-[92px] h-full aspect-square absolute top-0 right-0 !rounded-none hover:rounded-none !bg-inherit hover:!bg-neutral-4"
                            asChild
                          ></DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            side="left"
                            alignOffset={55}
                            sideOffset={-70}
                            className="rounded-xs"
                          >
                            <DropdownMenuItem
                              className="!rounded-xs"
                              onClick={() =>
                                navigate(
                                  generatePath(AppRoutes.REGULATORY_INTAKE_DETAILS, {
                                    id: job.id,
                                  }),
                                )
                              }
                            >
                              <InfoCircleIcon className="size-4 text-neutral-9" />
                              <span className="text-neutral-9">View Details</span>
                            </DropdownMenuItem>
                            {job.status === JobStatus.Failed && (
                              <DropdownMenuItem
                                onClick={() => jobRetry(job.id)}
                                disabled={isJobRetryPending}
                              >
                                <RefreshCcw01Icon className="size-4 text-neutral-9" />
                                <span className="text-neutral-9">Retry</span>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            )}
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
    </div>
  );
};
